import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, ShieldAlert, Loader2, Inbox, BadgeCheck } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { adminApi, type EmailAuditRow } from '@/services/adminApi';
import { useAdminCursorList } from '@/hooks/admin/useAdminCursorList';
import { AdminListHeader, ActiveFiltersBar } from '@/components/admin/AdminListToolbar';
import { AdminListFooter } from '@/components/admin/AdminListFooter';
import { SortableTableHeader, type SortDir } from '@/components/admin/SortableTableHeader';

/**
 * Sprint 6 — Email audit dashboard.
 * Reads `/admin/email-audit` (paginated audit rows) + `/admin/email-audit/kpi`
 * (last N days delivery / bounce / complaint rates).
 */
const STATUSES: Array<EmailAuditRow['status']> = ['sent', 'delivered', 'failed', 'suppressed', 'bounced', 'complained'];
const KNOWN_TEMPLATES = [
  'newApplicationAlert', 'screeningCompletedDigest', 'interviewScheduledRecruiterAlert',
  'interviewCancelledRecruiterAlert', 'candidateHiredAlert', 'candidateRejectedAlert',
  'candidateWithdrawnAlert', 'offerDeclinedAlert', 'interviewReportNotification',
  'invitationResponse', 'invitationEmail', 'candidateConfirmation', 'interviewInvitation',
  'interviewRescheduled', 'interviewCancelled', 'screeningNotification', 'actionNotification',
  'trialDay1Welcome', 'trialDay3Features', 'trialDay5CaseStudy', 'trialDay6LastChance', 'trialDay8Blocked',
  'capWarning', 'capReached',
  'subscriptionActivated', 'subscriptionUpgraded', 'subscriptionDowngraded',
  'subscriptionCancellationScheduled', 'subscriptionCanceledEffective', 'subscriptionUncanceled',
  'paymentFailedRetry', 'paymentFailedFinal', 'cardExpiringSoon', 'accountSuspendedForPayment',
  'welcomePostSignup', 'emailVerification', 'passwordResetSelfService', 'accountLockedSecurity',
  'memberRemoved', 'adminDigestSignupMrr', 'adminAlertFirstPaid', 'adminDigestDunning',
];

const STATUS_VARIANT: Record<NonNullable<EmailAuditRow['status']>, { icon: typeof CheckCircle2; color: string; label: string }> = {
  sent: { icon: Inbox, color: 'text-blue-500', label: 'Sent' },
  delivered: { icon: BadgeCheck, color: 'text-success', label: 'Delivered' },
  failed: { icon: XCircle, color: 'text-destructive', label: 'Failed' },
  suppressed: { icon: ShieldAlert, color: 'text-warning', label: 'Suppressed' },
  bounced: { icon: AlertTriangle, color: 'text-destructive', label: 'Bounced' },
  complained: { icon: AlertTriangle, color: 'text-destructive', label: 'Complained' },
};

export default function AdminEmailAuditPage() {
  const [status, setStatus] = useState<string>('all');
  const [template, setTemplate] = useState<string>('all');
  const [recipientType, setRecipientType] = useState<string>('all');
  const [companyId, setCompanyId] = useState('');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [searchInline, setSearchInline] = useState('');
  const [windowDays, setWindowDays] = useState(7);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const onSortChange = (field: string | null, dir: SortDir) => { setSortBy(field); setSortDir(dir); };

  const filterParams = useMemo(() => ({
    ...(status !== 'all' ? { status } : {}),
    ...(template !== 'all' ? { template } : {}),
    ...(recipientType !== 'all' ? { recipientType } : {}),
    ...(companyId ? { companyId } : {}),
    ...(since ? { since } : {}),
    ...(until ? { until } : {}),
    ...(sortBy ? { sortBy, sortDir } : {}),
  }), [status, template, recipientType, companyId, since, until, sortBy, sortDir]);

  const fetchPage = useCallback(
    (cursor: string | null) => adminApi.emailAudit.list({ ...filterParams, cursor }),
    [filterParams],
  );
  const {
    items, totalCount, loading, isRefreshing, error, refresh,
    hasMore, loadingMore, loadMore,
  } = useAdminCursorList<EmailAuditRow>(fetchPage, [filterParams]);

  const [kpi, setKpi] = useState<Awaited<ReturnType<typeof adminApi.emailAudit.kpi>> | null>(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setKpiLoading(true);
    adminApi.emailAudit.kpi(windowDays)
      .then((r) => { if (!cancelled) setKpi(r); })
      .catch((e) => { console.warn('email audit kpi failed', e); })
      .finally(() => { if (!cancelled) setKpiLoading(false); });
    return () => { cancelled = true; };
  }, [windowDays]);

  const filtered = useMemo(() => {
    if (!searchInline) return items;
    const s = searchInline.toLowerCase();
    return items.filter((r) =>
      (r.template ?? '').toLowerCase().includes(s)
      || (r.to ?? r.toMasked ?? '').toLowerCase().includes(s)
      || (r.companyId ?? '').toLowerCase().includes(s)
      || (r.messageId ?? '').toLowerCase().includes(s),
    );
  }, [items, searchInline]);

  const activeFilters: Array<{ key: string; label: string; onClear: () => void }> = [];
  if (status !== 'all') activeFilters.push({ key: 'status', label: `Status: ${status}`, onClear: () => setStatus('all') });
  if (template !== 'all') activeFilters.push({ key: 'tpl', label: `Template: ${template}`, onClear: () => setTemplate('all') });
  if (recipientType !== 'all') activeFilters.push({ key: 'rt', label: `Recipient: ${recipientType}`, onClear: () => setRecipientType('all') });
  if (companyId) activeFilters.push({ key: 'cid', label: `Company: ${companyId.slice(0, 8)}…`, onClear: () => setCompanyId('') });
  if (since) activeFilters.push({ key: 'since', label: `Since ${since}`, onClear: () => setSince('') });
  if (until) activeFilters.push({ key: 'until', label: `Until ${until}`, onClear: () => setUntil('') });

  return (
    <div className="space-y-4">
      <AdminListHeader
        title="Email Audit"
        subtitle="sentEmails — every email send (deliveries / bounces / complaints) tracked here"
        loading={loading}
        isRefreshing={isRefreshing}
        onRefresh={refresh}
      />

      {/* KPI */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">Last {windowDays} days</div>
            <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
              <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24h</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {kpiLoading ? (
            <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" />Loading KPI…</div>
          ) : kpi ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Total attempts" value={kpi.totalAttempts.toString()} />
              <KpiCard label="Delivery rate" value={`${kpi.deliveryRate}%`} variant={kpi.deliveryRate >= 95 ? 'success' : kpi.deliveryRate >= 90 ? 'warning' : 'destructive'} />
              <KpiCard label="Bounce rate" value={`${kpi.bounceRate}%`} variant={kpi.bounceRate <= 2 ? 'success' : kpi.bounceRate <= 5 ? 'warning' : 'destructive'} />
              <KpiCard label="Complaint rate" value={`${kpi.complaintRate}%`} variant={kpi.complaintRate <= 0.1 ? 'success' : 'warning'} />
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">KPI unavailable</div>
          )}
          {kpi && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 pt-2 border-t border-border">
              {STATUSES.map((s) => {
                const variant = STATUS_VARIANT[s!];
                const Icon = variant.icon;
                const count = kpi.totals[s as keyof typeof kpi.totals] ?? 0;
                return (
                  <div key={s} className="flex items-center gap-2 text-xs">
                    <Icon className={`h-3 w-3 ${variant.color}`} />
                    <span className="text-muted-foreground">{variant.label}:</span>
                    <span className="font-mono font-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s!}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Template" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All templates</SelectItem>
              {KNOWN_TEMPLATES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={recipientType} onValueChange={setRecipientType}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Recipient" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All recipients</SelectItem>
              <SelectItem value="user">Internal user</SelectItem>
              <SelectItem value="candidate">Candidate</SelectItem>
              <SelectItem value="invitee">Invitee</SelectItem>
              <SelectItem value="internal">Internal ops</SelectItem>
            </SelectContent>
          </Select>
          <Input className="h-9" placeholder="Company ID" value={companyId} onChange={(e) => setCompanyId(e.target.value.trim())} />
          <Input className="h-9" type="date" placeholder="Since" value={since} onChange={(e) => setSince(e.target.value)} />
          <Input className="h-9" type="date" placeholder="Until" value={until} onChange={(e) => setUntil(e.target.value)} />
        </CardContent>
      </Card>

      <ActiveFiltersBar filters={activeFilters} />

      {/* Inline search */}
      <div>
        <Input
          className="h-9 max-w-md"
          placeholder="Search inline (template / recipient / companyId / messageId)"
          value={searchInline}
          onChange={(e) => setSearchInline(e.target.value)}
        />
      </div>

      {/* Table */}
      {error ? (
        <Card><CardContent className="p-4 text-sm text-destructive">{error}</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <SortableTableHeader label="Time" field="ts" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} />
                  </th>
                  <th className="px-3 py-2 text-left">
                    <SortableTableHeader label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="px-3 py-2 text-left">
                    <SortableTableHeader label="Template" field="template" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="px-3 py-2 text-left">Recipient</th>
                  <th className="px-3 py-2 text-left">Company</th>
                  <th className="px-3 py-2 text-left">Locale</th>
                  <th className="px-3 py-2 text-left">Message ID</th>
                  <th className="px-3 py-2 text-left">Error</th>
                </tr>
              </thead>
              <tbody>
                {loading && filtered.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-muted-foreground"><Loader2 className="inline h-3 w-3 animate-spin mr-1" />Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-xs text-muted-foreground">No emails matching the current filters.</td></tr>
                ) : (
                  filtered.map((r) => {
                    const variant = r.status ? STATUS_VARIANT[r.status] : null;
                    const Icon = variant?.icon ?? Inbox;
                    return (
                      <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                        <td className="px-3 py-2 text-xs font-mono text-muted-foreground whitespace-nowrap">
                          {r.ts ? new Date(r.ts).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'medium' }) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="gap-1">
                            <Icon className={`h-3 w-3 ${variant?.color ?? ''}`} />
                            <span className="text-xs">{variant?.label ?? r.status ?? '—'}</span>
                          </Badge>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{r.template ?? '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {r.to ?? r.toMasked ?? '—'}
                          {!r.to && r.toMasked && (
                            <span className="block text-[9px] text-muted-foreground italic mt-0.5">
                              email mascherata (log pre-30/05/26)
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{r.companyId ? <code className="text-xs">{r.companyId.slice(0, 10)}…</code> : '—'}</td>
                        <td className="px-3 py-2 text-xs uppercase">{r.locale ?? '—'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.messageId ? <code className="text-xs">{r.messageId.slice(0, 12)}…</code> : '—'}</td>
                        <td className="px-3 py-2 text-xs text-destructive max-w-xs truncate" title={r.error ?? ''}>{r.error ?? '—'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <AdminListFooter
              loadedCount={items.length}
              filteredCount={searchInline ? filtered.length : undefined}
              totalCount={totalCount}
              entityLabel="email"
              entityLabelPlural="email"
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
            />
          </CardContent>
        </Card>
      )}
      {isRefreshing && <p className="text-xs text-muted-foreground text-right">Aggiornamento...</p>}
    </div>
  );
}

function KpiCard({ label, value, variant = 'default' }: { label: string; value: string; variant?: 'default' | 'success' | 'warning' | 'destructive' }) {
  const colorClass = variant === 'success' ? 'text-success' : variant === 'warning' ? 'text-warning' : variant === 'destructive' ? 'text-destructive' : '';
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${colorClass}`}>{value}</div>
    </div>
  );
}
